import { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, Line, Marker } from 'react-simple-maps';
import { 
  Activity, Tag, BarChart3, CheckCircle, AlertTriangle, TrendingUp, 
  Box, Sparkles, Palette, Video, Film, Wand2, Moon, Sun 
} from 'lucide-react';
import worldData from '../data/world-110m.json'; 

// ==========================================
// MOCK DATA (Pure JS - No TypeScript types)
// ==========================================
const MOCK_CONTENT_TYPES = [
  { name: 'DeepFake', value: 18, color: '#3b82f6' },
  { name: 'Face Swap', value: 15, color: '#8b5cf6' },
  { name: 'Voice Clone', value: 12, color: '#f59e0b' },
  { name: 'Movie Shorts', value: 9, color: '#06b6d4' },
  { name: 'Travel', value: 10, color: '#10b981' },
  { name: 'Political', value: 8, color: '#ef4444' },
  { name: 'Kids', value: 7, color: '#ec4899' },
  { name: 'Propaganda', value: 6, color: '#84cc16' },
  { name: 'Education', value: 6, color: '#14b8a6' },
  { name: 'Adult', value: 5, color: '#6b7280' },
  { name: 'Other', value: 4, color: '#64748b' },
];

const MOCK_CREATOR_TOOLS = [
  { rank: 1, tool: 'Midjourney', uploadCount: 3300, icon: Sparkles },
  { rank: 2, tool: 'Photoshop', uploadCount: 474, icon: Palette },
  { rank: 3, tool: 'FFmpeg', uploadCount: 358, icon: Video },
  { rank: 4, tool: 'Final Cut', uploadCount: 339, icon: Film },
  { rank: 5, tool: 'Runway', uploadCount: 163, icon: Wand2 },
  { rank: 6, tool: 'DALL-E', uploadCount: 137, icon: Sparkles },
  { rank: 7, tool: 'Stable Diffusion', uploadCount: 298, icon: Box },
];

const MOCK_TOP_COUNTRIES = [
  { country: 'United States', percentage: 29 },
  { country: 'China', percentage: 27 },
  { country: 'United Kingdom', percentage: 26 },
  { country: 'Germany', percentage: 13 },
  { country: 'India', percentage: 10 },
  { country: 'Brazil', percentage: 9 },
  { country: 'Japan', percentage: 7 },
];

const MOCK_TAG_ADOPTION = [
  { tag: '#DeepFake', growth: 4.8 },
  { tag: '#travelvlog', growth: 1.2 },
  { tag: '#political', growth: -1.5 },
  { tag: '#cooking', growth: 2.3 },
  { tag: '#Propaganda', growth: 1.8 },
];

const MOCK_GEO_DATA = {
  'United States': { uploads: 450, riskLevel: 'low', trustScore: 0.92 },
  'China': { uploads: 380, riskLevel: 'medium', trustScore: 0.78 },
  'Germany': { uploads: 220, riskLevel: 'low', trustScore: 0.91 },
  'Brazil': { uploads: 180, riskLevel: 'low', trustScore: 0.87 },
  'United Kingdom': { uploads: 280, riskLevel: 'low', trustScore: 0.89 },
  'India': { uploads: 320, riskLevel: 'medium', trustScore: 0.82 },
  'Russia': { uploads: 190, riskLevel: 'high', trustScore: 0.65 },
  'Japan': { uploads: 260, riskLevel: 'low', trustScore: 0.94 },
};

const MOCK_ARCS = [
  { id: 1, from: [-74.0, 40.7], to: [-0.12, 51.5], color: '#3b82f6' },
  { id: 2, from: [-0.12, 51.5], to: [139.7, 35.7], color: '#8b5cf6' },
  { id: 3, from: [139.7, 35.7], to: [-122.4, 37.8], color: '#10b981' },
  { id: 4, from: [-122.4, 37.8], to: [2.35, 48.85], color: '#f59e0b' },
];

const CITIES = {
  'New York': [-74.0, 40.7],
  'London': [-0.12, 51.5],
  'Tokyo': [139.7, 35.7],
  'San Francisco': [-122.4, 37.8],
  'Paris': [2.35, 48.85],
  'Sydney': [151.2, -33.85],
};

// ==========================================
// SUB-COMPONENTS
// ==========================================

const GlassPanel = ({ title, children, className = '' }) => (
  <div className={`bg-white/10 dark:bg-gray-900/80 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-lg ${className}`}>
    <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{title}</h3>
    {children}
  </div>
);

const ScrollableList = ({ children }) => (
  <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-2">
    {children}
  </div>
);

// ==========================================
// MAIN COMPONENT
// ==========================================

const StandaloneMap = () => {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, country: '', data: null });
  const [stats, setStats] = useState({
    cumulativeUsers: 1247,
    aiDetectionRate: 94.2,
    humanCreated: 78.5,
    trustedContent: 91.8,
  });
  const [activityFeed, setActivityFeed] = useState([
    { id: '1', time: '10:42:15', type: 'upload', desc: 'Admin uploaded media', country: 'US' },
    { id: '2', time: '10:41:02', type: 'alert', desc: 'Risk alert: Inappropriate content', country: 'CN' },
    { id: '3', time: '10:39:45', type: 'milestone', desc: 'Global milestone: 10k analyses', country: 'GLOBAL' },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        cumulativeUsers: prev.cumulativeUsers + Math.floor(Math.random() * 5),
        aiDetectionRate: Math.min(99.9, prev.aiDetectionRate + (Math.random() * 0.2 - 0.1)),
        humanCreated: Math.max(70, Math.min(85, prev.humanCreated + (Math.random() * 0.3 - 0.15))),
        trustedContent: Math.min(99.9, prev.trustedContent + (Math.random() * 0.15 - 0.075)),
      }));

      const newEvent = {
        id: Date.now().toString(),
        time: new Date().toLocaleTimeString('en-US', { hour12: false }),
        type: Math.random() > 0.6 ? 'alert' : 'upload',
        desc: Math.random() > 0.6 ? 'Risk alert: Anomaly detected' : 'New media file processed',
        country: ['US', 'GB', 'DE', 'JP', 'CN'][Math.floor(Math.random() * 5)],
      };
      setActivityFeed(prev => [newEvent, ...prev.slice(0, 7)]);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  const getRiskColor = (level) => {
    if (level === 'low') return 'text-emerald-400';
    if (level === 'medium') return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className={`${isDarkMode ? 'dark' : ''} w-full h-screen flex flex-col bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden`}>
      
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 3px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #374151; }
      `}</style>

      {/* ================= HEADER ================= */}
      <header className="flex items-center justify-between px-6 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
          <h1 className="text-sm font-bold uppercase tracking-widest">Live Telemetry Map</h1>
        </div>
        
        <div className="flex items-center gap-8">
          <div className="flex gap-6 text-right">
            <div>
              <p className="text-[10px] uppercase text-gray-500 dark:text-gray-400 tracking-wider">Cumulative Users</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.cumulativeUsers.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-500 dark:text-gray-400 tracking-wider">AI Detection</p>
              <p className="text-lg font-bold text-blue-500">{stats.aiDetectionRate.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-500 dark:text-gray-400 tracking-wider">Human-Created</p>
              <p className="text-lg font-bold text-yellow-500">{stats.humanCreated.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-500 dark:text-gray-400 tracking-wider">Trusted Content</p>
              <p className="text-lg font-bold text-emerald-500">{stats.trustedContent.toFixed(1)}%</p>
            </div>
          </div>
          <button 
            onClick={toggleTheme}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>
      </header>

      {/* ================= MAIN LAYOUT ================= */}
      <div className="flex flex-1 overflow-hidden">
        
        {/* LEFT SIDEBAR: LIVE TELEMETRY */}
        <aside className="w-72 flex flex-col gap-4 p-4 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-y-auto custom-scrollbar shrink-0">
          <h2 className="text-lg font-bold uppercase tracking-wider mb-2">Live Telemetry</h2>
          
          <GlassPanel title="Content Types">
            <ScrollableList>
              {MOCK_CONTENT_TYPES.map((type, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }} />
                    <span className="text-gray-800 dark:text-gray-200 font-medium">{type.name}</span>
                  </div>
                  <span className="text-gray-500 dark:text-gray-400 font-semibold">{type.value}%</span>
                </div>
              ))}
            </ScrollableList>
          </GlassPanel>

          <GlassPanel title="Top Tools">
            <ScrollableList>
              {MOCK_CREATOR_TOOLS.map((tool) => {
                const Icon = tool.icon || Box;
                return (
                  <div key={tool.rank} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400 w-4">#{tool.rank}</span>
                      <Icon className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-800 dark:text-gray-200 font-medium truncate">{tool.tool}</span>
                    </div>
                    <span className="text-emerald-500 font-semibold">{tool.uploadCount}</span>
                  </div>
                );
              })}
            </ScrollableList>
          </GlassPanel>
        </aside>

        {/* CENTER: MAP & BOTTOM FEED */}
        <div className="flex-1 flex flex-col min-w-0 bg-gray-100 dark:bg-gray-950 relative">
          
          <div className="flex-1 relative">
            <ComposableMap projectionConfig={{ scale: 165, center: [10, 15] }} className="w-full h-full">
              <Geographies geography={worldData}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const countryName = geo.properties.name;
                    const countryData = MOCK_GEO_DATA[countryName];
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={countryData ? (countryData.riskLevel === 'high' ? '#ef444433' : countryData.riskLevel === 'medium' ? '#f59e0b33' : '#10b98133') : 'var(--map-fill, #101e3c)'}
                        stroke="var(--map-stroke, #2f5f8f)"
                        strokeWidth={0.6}
                        style={{
                          default: { outline: 'none' },
                          hover: { outline: 'none', fill: 'var(--map-glow, rgba(80, 200, 255, 0.25))', cursor: 'pointer' },
                          pressed: { outline: 'none' },
                        }}
                        onMouseEnter={(event) => setTooltip({ visible: true, x: event.clientX, y: event.clientY, country: countryName, data: countryData })}
                        onMouseMove={(event) => setTooltip(prev => ({ ...prev, x: event.clientX, y: event.clientY }))}
                        onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                      />
                    );
                  })
                }
              </Geographies>
              
              {MOCK_ARCS.map((arc) => (
                <Line key={arc.id} from={arc.from} to={arc.to} stroke={arc.color} strokeWidth={1.2} strokeLinecap="round" strokeDasharray="4 4" style={{ opacity: 0.6 }} />
              ))}

              {Object.entries(CITIES).map(([name, coords]) => (
                <Marker key={name} coordinates={coords}>
                  <circle r={2} fill="#6fd1ff" opacity={0.85} />
                  <circle r={2} fill="#6fd1ff" opacity={0.35}>
                    <animate attributeName="r" from="2" to="8" dur="2.5s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.35" to="0" dur="2.5s" repeatCount="indefinite" />
                  </circle>
                </Marker>
              ))}
            </ComposableMap>

            {tooltip.visible && (
              <div 
                className="fixed z-50 pointer-events-none bg-gray-900/95 dark:bg-black/90 text-white text-xs rounded-lg shadow-xl border border-gray-700 p-3 backdrop-blur-sm"
                style={{ left: tooltip.x + 15, top: tooltip.y + 15 }}
              >
                <p className="font-bold text-sm mb-1 text-blue-400">{tooltip.country}</p>
                {tooltip.data ? (
                  <>
                    <p className="text-gray-300">Uploads: <span className="text-white font-semibold">{tooltip.data.uploads}</span></p>
                    <p className="text-gray-300">Trust Score: <span className="text-white font-semibold">{(tooltip.data.trustScore * 100).toFixed(0)}%</span></p>
                    <p className={`font-semibold capitalize ${getRiskColor(tooltip.data.riskLevel)}`}>Risk: {tooltip.data.riskLevel}</p>
                  </>
                ) : (
                  <p className="text-gray-400 italic">No telemetry data</p>
                )}
              </div>
            )}
          </div>

          <div className="h-48 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4 shrink-0 flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Live Activity Feed</h3>
              <span className="text-xs font-bold text-blue-500 uppercase tracking-wider">Now</span>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2">
              {activityFeed.map((event) => (
                <div key={event.id} className="flex items-center gap-3 text-xs p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${event.type === 'alert' ? 'bg-red-500' : event.type === 'milestone' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                  <span className="text-gray-500 dark:text-gray-400 font-mono w-16 shrink-0">[{event.time}]</span>
                  <span className={`font-medium flex-1 ${event.type === 'alert' ? 'text-red-500' : 'text-gray-800 dark:text-gray-200'}`}>{event.desc}</span>
                  <span className="text-gray-400 text-[10px] shrink-0">[{event.country}]</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT SIDEBAR: LIVE METRICS */}
        <aside className="w-72 flex flex-col gap-4 p-4 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 overflow-y-auto custom-scrollbar shrink-0">
          <h2 className="text-lg font-bold uppercase tracking-wider mb-2">Live Metrics</h2>
          
          <GlassPanel title="Top Countries">
            <ScrollableList>
              {MOCK_TOP_COUNTRIES.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs mb-2">
                  <span className="w-24 text-gray-800 dark:text-gray-200 font-medium truncate">{item.country}</span>
                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${item.percentage}%` }} />
                  </div>
                  <span className="w-8 text-right text-gray-500 dark:text-gray-400 font-semibold">{item.percentage}%</span>
                </div>
              ))}
            </ScrollableList>
          </GlassPanel>

          <GlassPanel title="System Status">
            <ul className="space-y-3 text-xs">
              <li className="flex items-center justify-between">
                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500" /> API Gateway</div>
                <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded font-bold">OK</span>
              </li>
              <li className="flex items-center justify-between">
                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500" /> AI Engine</div>
                <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded font-bold">OK</span>
              </li>
              <li className="flex items-center justify-between">
                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-yellow-500" /> CDN</div>
                <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded font-bold">DEGRADED</span>
              </li>
              <li className="flex items-center justify-between">
                <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Auth Service</div>
                <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded font-bold">OK</span>
              </li>
            </ul>
          </GlassPanel>

          <GlassPanel title="Trending Tags">
            <ScrollableList>
              {MOCK_TAG_ADOPTION.map((tag, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs p-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg">
                  <span className="text-gray-800 dark:text-gray-200 font-medium">{tag.tag}</span>
                  <span className={`flex items-center gap-1 ${tag.growth >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    <TrendingUp className="w-3 h-3" />
                    {tag.growth > 0 ? '+' : ''}{tag.growth}%
                  </span>
                </div>
              ))}
            </ScrollableList>
          </GlassPanel>
        </aside>

      </div>
    </div>
  );
};

export default StandaloneMap;